import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, FormControl } from '@angular/forms';
import { Workflow, TaskDefinition } from '@models/workflow.model';
import { PurchaseOrderDto } from '@app/models/purchase-order.model.dto';
import { User } from '@models/user.model';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CompanyService } from '@services/company.service';
import { Toaster } from '@services/toaster.service';
import { LocalStorage } from 'ngx-webstorage';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import CONFIG from '@app/app.config';
import { environment } from '@env/environment';
import Swal from 'sweetalert2';

@Component({
  selector: 'ngx-apply-workflow',
  templateUrl: './apply-workflow-modal.component.html',
  styleUrls: ['./apply-workflow-modal.component.scss'],
})
export class ApplyWorkflowModalComponent implements OnInit, OnDestroy {
  isRCDSS = environment.name === 'rcdss';

  @Input() organizationId;
  @Input() workflow: Workflow;
  @Input() tasks: TaskDefinition[];

  customerUsers: User[];
  users: User[];
  contracts: PurchaseOrderDto[];
  tasksPendingResponsible: TaskDefinition[];

  showTasksStep: boolean = false;

  resourcesToApply: ResourcesToTasks[] = [];

  loading: boolean = false;
  loadingMessage: string = '';

  constructor(
    private ngbActiveModal: NgbActiveModal,
    private fb: FormBuilder,
    private $company: CompanyService,
    private $toaster: Toaster,
  ) {
  }

  ngOnInit() {
    this.tasksPendingResponsible = this.tasks.filter(t => !t?.default_responsible_id);
    this.getContracts();

    this.$company.users(this.organizationId).subscribe(users => {
      this.customerUsers = users;
    }, (error) => {
      this.$toaster.show('error', 'An error occurred', 'Please try again later');
      console.error(error);
    });
  }

  getContracts() {
    this.$company.commissionedPOsTiny(this.organizationId).subscribe(res => {
      this.contracts = res;
    }, (error) => {
      console.error(error);
    });
  }

  back() {
    this.showTasksStep = false;
  }

  next() {
    if (this.areAllResponsiblesSet()) {
      this.save();
      return;
    }

    this.showTasksStep = true;
    const uniqueVendorIds = [...new Set(this.resourcesToApply.map(item => item.supplier_id))];
    this.loadUsers(uniqueVendorIds);
  }

  loadUsers(vendorIds) {
    this.users = [...this.customerUsers];
    vendorIds.forEach(vendorId => {
      this.$company.users(vendorId).subscribe(users => {
        this.users.push(...users);
      }, (error) => {
        this.$toaster.show('error', 'An error occurred', 'Some functions may not work as expected');
        console.error(error);
      });
    });
  }

  // refactor: keep error logs and show in modal
  // refactor: slow down loading screen and make sure loading message is showing
  save() {
    if (!this.areAllResponsiblesSet()) {
      Swal.fire({
        title: 'Confirmation needed',
        text: `You have not set a responsible for all tasks.
          Tasks that don't have a responsible will be automatically assigned to the primary contact
          of the company intended to complete it. Do you want to continue?`,
        icon: 'question',
        cancelButtonText: 'Go back',
        showCancelButton: true,
        buttonsStyling: false,
        confirmButtonText: 'Continue',
        customClass: {
          confirmButton: 'btn btn-primary mr-2',
          cancelButton: 'btn btn-secondary ml-2',
        },
        confirmButtonColor: '#41b146',
      }).then((result) => {
        if (result.isConfirmed === false) {
          return;
        }
        this.applyWorkflow();
      });
      return;
    }

    this.applyWorkflow();
  }

  applyWorkflow() {
    this.loading = true;

    let resourceName = this.isRCDSS ? 'Contract' : 'Purchase Order';
    let errors = 0;

    this.resourcesToApply.forEach(r => {
      this.loadingMessage = `Applying workflow ${this.workflow.name} to ${resourceName} # ${r.doc_number}...`;
      let data = {resposibles: r.tasks_pending_responsible} as unknown as {responsibles: {task_id:string, responsible_id:string}[]};
      this.$company.applyWorkflow(this.organizationId, r.doc_id, this.workflow.id, data)
        .subscribe(res => {
          //
        }, error => {
          errors++;
          console.error(error);
          this.$toaster.show('error', 'An error occurred', 'Please try again later');
        });
    });

    if (errors > 0) {
      this.$toaster.show('warning', 'An error occurred', `The workflow was not applied for some ${resourceName}s`);
    } else {
      this.$toaster.show('success', 'Success', `Workflow applied to all selected ${resourceName}s.`);
    }

    this.loading = false;
    this.loadingMessage = '';
    this.closeModal();
  }

  setForAppliance($event, resource) {
    let resourceToTasks: ResourcesToTasks
    resourceToTasks = this.resourcesToApply.find(r => r.doc_id === resource.id);

    if(!$event.checked) {
      if (resourceToTasks) {
        this.resourcesToApply = this.resourcesToApply.filter(r => {
          return r.doc_id !== resourceToTasks.doc_id;
        });
      }
      return;
    }

    if (!resourceToTasks) {
      resourceToTasks = {
        doc_id: resource.id,
        supplier_id: resource.supplier_id,
        supplier_name: resource.supplier_name,
        doc_number: resource.customer_po_number,
        rcdss_contract_program: resource?.rcdss_contract_program,
        status: resource.status,
        tasks_pending_responsible: [],
      };
    }

    this.tasksPendingResponsible.forEach(t => {
      resourceToTasks.tasks_pending_responsible.push({doc_id: resource.id, task_id: t.id, task_name: t.name});
    });

    this.resourcesToApply.push(resourceToTasks);
  }

  setResponsible($event, task) {
    let i = this.resourcesToApply.findIndex(r => r.doc_id === task.doc_id);
    if (i < 0) {
      return;
    }
    let taskIndex = this.resourcesToApply[i].tasks_pending_responsible.findIndex(t => t.task_id === task.task_id);
    this.resourcesToApply[i].tasks_pending_responsible[taskIndex].responsible_id = $event.value;
  }

  areAllResponsiblesSet() {
    for (const r of this.resourcesToApply) {
      let i = r.tasks_pending_responsible.some(t => !t.responsible_id);
      if (i) {
        return false;
      }
    }
    return true;
  }

  getRelevantUsers(taskId) {
    let t = this.getTask(taskId);
    return this.users.filter(u => {
      if (t.intended_for_role === 'customers' && u.company.id === this.organizationId) {
        return true;
      }
      if (t.intended_for_role === 'suppliers' && u.company.id !== this.organizationId) {
        return true;
      }
      return false;
    });
  }

  getTask(taskId) {
    return this.tasks.find(t => t.id === taskId);
  }

  closeModal() {
    this.ngbActiveModal.dismiss();
  }

  ngOnDestroy(): void {
  }

}

class ResourcesToTasks {
  doc_id?: string;
  supplier_id?: string;
  supplier_name?: string;
  doc_number?: string;
  rcdss_contract_program?: string;
  status?: string;

  task_id?:string;
  task_name?:string;
  responsible_id?:string;

  tasks_pending_responsible?: ResourcesToTasks[]; 
}